import React, { useState, useEffect, useCallback } from "react"
import { Space, Checkbox, Button, Typography, Tag, Divider, Tooltip, Row, Col, Badge, message, Form, Input, Modal, Popconfirm, Radio } from "antd"
import PropTypes from "prop-types"
import {
  DatabaseOutlined,
  SwapOutlined,
  EnvironmentOutlined,
  BookOutlined,
  AimOutlined,
  LinkOutlined,
  SafetyCertificateOutlined,
  LockOutlined,
  MessageOutlined,
  TeamOutlined,
  EyeOutlined,
  GlobalOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  HistoryOutlined,
  FileTextOutlined,
  EditOutlined,
  CommentOutlined,
  DownOutlined,
  UpOutlined,
  SaveOutlined,
  CloseOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined
} from "@ant-design/icons"
import moment from "moment"
import cssStyles from './Monster.module.css'
import { useCallAPI } from "../../../../../hooks/useCallAPI"
import { MONSTER_NOTES_GET, MONSTER_NOTES_ADD, MONSTER_NOTES_UPDATE, MONSTER_NOTES_DELETE } from "../../../../../ApiUrl"
import { useNotes } from '../context/notesContext';
import axios from 'axios';
import { requestHeader } from "../../../../../others/header"

const { Text, Title, Paragraph } = Typography
const { TextArea } = Input;


const themeColors = {
  primary: '#1890ff',
  secondary: '#40a9ff',
  tertiary: '#096dd9',
  background: '#e6f7ff',
  border: '#91d5ff'
}

const defaultStyles = {
  positionTitle: {
    fontWeight: 600,
    fontSize: "14px",
    lineHeight: "1.2",
    color: "#333",
  },
  companyName: {
    fontSize: "14px",
    lineHeight: "1.2",
    color: "#666",
  },
  tag: {
    fontSize: "12px",
    lineHeight: "1.2",
  },
  candidateCard: {
    background: "#fff",
    padding: "16px",
    borderRadius: "6px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
    border: "1px solid #f0f0f0",
    transition: "all 0.3s ease",
  },
  selectedCard: {
    border: "1px solid #1890ff",
    boxShadow: "0 2px 8px rgba(24, 144, 255, 0.2)",
  },
  viewedCard: {
    borderLeft: "4px solid #52c41a",
    boxShadow: "0 2px 8px rgba(82, 196, 26, 0.15)",
  },
  cardContent: {
    width: "100%",
  },
  matchPercentage: {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: "10px",
    fontSize: "12px",
    fontWeight: "bold",
    color: "#fff",
    textAlign: "center",
    minWidth: "60px",
  },
  nameContainer: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
  }
}

const actionBarStyles = {
  position: "absolute",
  bottom: "-20px",
  left: "16px",
  display: "flex",
  alignItems: "center",
  zIndex: 1,
  transition: "opacity 0.3s ease, transform 0.3s ease",
}

const cardWrapperStyles = {
  position: "relative",
  marginBottom: "16px",
}

const CandidateCard = React.memo(
  ({
    candidate,
    isSelected = false,
    onCardClick,
    onCompare,
    compareList = [],
    onExternalView,
    useStyles = defaultStyles,
  }) => {

    const [showActions, setShowActions] = useState(false)
    const [isLocallyViewed, setIsLocallyViewed] = useState(false)
    const [notesData, setNotesData] = useState([])
    const [hasNotes, setHasNotes] = useState(false)
    const [notesExpanded, setNotesExpanded] = useState(false)
    const [isAddingNote, setIsAddingNote] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [editingNoteId, setEditingNoteId] = useState(null)
    const [form] = Form.useForm();
    const addNoteApi = useCallAPI(MONSTER_NOTES_ADD);
    const updateNoteApi = useCallAPI(MONSTER_NOTES_UPDATE);
    // const deleteNoteApi = useCallAPI(MONSTER_NOTES_DELETE);
    const { syncNotes, addNote, updateNote, deleteNote, getCandidateNotes, getLastUpdated, getCandidateDbId, syncCandidateDbId } = useNotes();
    const candidateId = candidate?.sid || candidate?.id;

    const deleteNotes = async (id) => {
      try {
        const candidateDbId = getCandidateDbId(candidate?.sid || candidate?.id) || candidate?.candidateId;

        const res = axios.delete(`${MONSTER_NOTES_DELETE}/${candidateId}/${id}?candidateId=${candidateDbId || null}`, requestHeader())
        return res
      }
      catch (error) {
        console.error('error deleting note', error)
      }
    }
    useEffect(() => {
      if (candidate.isViewed) {
        setIsLocallyViewed(true)
      }
    }, [candidate.isViewed])
    useEffect(() => {
      if (candidate) {
        if (candidate.notesInfo) {
          const notes = candidate?.notesInfo?.notes || [];
          if (notes && notes.length > 0) {
            const validatedNotes = notes.map(note => ({
              ...note,
              type: note.type || 'notes'
            }));
            setNotesData(validatedNotes);
            setNotesExpanded(true);
          } else {
            setNotesData([]);
          }
          const hasNotesValue = candidate.notesInfo.hasNotes === true || notes.length > 0 ||
            (typeof candidate.notesInfo.notesCount === 'number' && candidate.notesInfo.notesCount > 0);
          setHasNotes(hasNotesValue);
          if (hasNotesValue && !notesExpanded) {
            setNotesExpanded(true);
          }
        } else {
          setNotesData([]);
          setHasNotes(false);
        }
      }
    }, [candidate]);
    useEffect(() => {
      if (!candidateId) return;
      if (candidate?.notesInfo) {
        const contextNotes = getCandidateNotes(candidateId);
        const candidateNotes = candidate.notesInfo.notes || [];
        if (contextNotes.length === 0 && candidateNotes.length > 0) {
          syncNotes(candidateId, candidateNotes);
          setNotesData(candidateNotes);
          setHasNotes(candidateNotes.length > 0);
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [candidateId]);
    useEffect(() => {
      if (!candidateId) return;
      const lastUpdateTime = getLastUpdated(candidateId);
      const contextNotes = getCandidateNotes(candidateId);
      if (
        contextNotes.length > 0 &&
        (notesData.length !== contextNotes.length || lastUpdateTime > 0)
      ) {
        setNotesData(contextNotes);
        setHasNotes(contextNotes.length > 0);
      }
    }, [candidateId, getCandidateNotes, getLastUpdated, notesData.length]);
    const toggleNotes = (e) => {
      if (e) e.stopPropagation();
      setNotesExpanded(!notesExpanded);
    }
    const handleAddNote = async (noteData) => {
      if (!candidateId) {
        message.error('Cannot add note: Missing candidate ID');
        return;
      }

      setIsSubmitting(true);
      try {
        const sid = candidateId;
        const payload = {
          notes: noteData.notes,
          type: noteData.type || 'notes'
        };
        if (candidate.candidateId) {
          payload.candidateId = candidate.candidateId;
        }
        const contextNotes = getCandidateNotes(candidateId);
        const existingNotes = contextNotes.length > 0 ? [...contextNotes] : [...notesData];
        const response = await addNoteApi.post(payload, sid);
        let newNote;
        let updatedNotes = [];

        if (response?.data?.data) {
          if (Array.isArray(response.data.data)) {
            if (response.data.data.length >= existingNotes.length) {
              updatedNotes = response.data.data;
            } else {
              newNote = response.data.data[0];
              updatedNotes = [...existingNotes, newNote];
            }
          }
          else if (response.data.data.notes && Array.isArray(response.data.data.notes)) {
            if (response.data.data.notes.length >= existingNotes.length) {
              updatedNotes = response.data.data.notes;
            } else {
              newNote = response.data.data.notes[0];
              updatedNotes = [...existingNotes, newNote];
            }
          }
          else {
            newNote = response.data.data;
            updatedNotes = [...existingNotes, newNote];
          }
          setNotesData(updatedNotes);
          setHasNotes(true);
          setTimeout(() => {
            syncNotes(candidateId, updatedNotes);
          }, 100);
        }
        message.success('Note added successfully');
        setIsAddingNote(false);
        setEditingNoteId(null);
        form.resetFields();

        return response;
      } catch (error) {
        console.error('[CandidateCard] Error adding note:', error);
        message.error('Failed to add note');
        throw error;
      } finally {
        setIsSubmitting(false);
      }
    }
    const handleUpdateNote = async (noteId, noteData) => {
      if (!noteId) {
        message.error('Cannot update note: Missing note ID');
        return;
      }
      const dbId = getCandidateDbId(candidateId) || candidate?.candidateId;

      setIsSubmitting(true);
      try {
        const sid = candidateId;
        const payload = {
          notes: noteData.notes,
          type: noteData.type || 'notes'
        };
        if (candidate.candidateId) {
          payload.candidateId = candidate.candidateId || dbId;
        }
        const response = await updateNoteApi.patch(payload, `${sid}/${noteId}`);
        let updatedNotes;
        if (response?.data?.data?.notes && Array.isArray(response.data.data.notes)) {
          updatedNotes = response.data.data.notes;
        } else {
          updatedNotes = notesData.map(note => {
            if (note._id === noteId) {
              return {
                ...note,
                ...payload,
                updated_at: new Date().toISOString()
              };
            }
            return note;
          });
        }


        setNotesData(updatedNotes);


        setTimeout(() => {
          syncNotes(candidateId, updatedNotes);
        }, 100);

        message.success('Note updated successfully');


        setIsAddingNote(false);
        setEditingNoteId(null);
        form.resetFields();

        return response;
      } catch (error) {
        console.error('[CandidateCard] Error updating note:', error);
        message.error('Failed to update note');
        throw error;
      } finally {
        setIsSubmitting(false);
      }
    }

    const handleDeleteNote = async (noteId) => {
      if (!candidateId) {
        message.error('Cannot delete note: Missing candidate ID');
        return;
      }

      setIsSubmitting(true);
      try {
        const response = await deleteNotes(noteId);

        let updatedNotes;

        if (response?.data?.data?.notes && Array.isArray(response.data.data.notes)) {
          updatedNotes = response.data.data.notes;
        } else if (response?.data?.data?.multipleNotes && Array.isArray(response.data.data.multipleNotes)) {
          updatedNotes = response.data.data.multipleNotes;
        } else {
          updatedNotes = notesData.filter(note => note._id !== noteId);
        }

        deleteNote(candidateId, noteId);

        setNotesData(updatedNotes);
        setHasNotes(updatedNotes.length > 0);

        if (candidate.notesInfo) {
          candidate.notesInfo.notes = updatedNotes;
          candidate.notesInfo.notesCount = updatedNotes.length;
          candidate.notesInfo.hasNotes = updatedNotes.length > 0;
        }

        setTimeout(() => {
          syncNotes(candidateId, updatedNotes);
        }, 150);
        message.success('Note deleted successfully');

        if (updatedNotes.length === 0) {
          setNotesExpanded(false);
          syncNotes(candidateId, []);
        }

        return response;
      } catch (error) {
        console.error('[Monster Card] Error deleting note:', error);
        message.error('Failed to delete note');
        throw error;
      } finally {
        setIsSubmitting(false);
      }
    };

    useEffect(() => {
      if (candidate?.candidateId) {
        // If candidate has candidateId, sync it to notes context
        syncCandidateDbId(candidateId, candidate.candidateId);
      }
    }, [candidate?.candidateId, candidateId, syncCandidateDbId]);

    const confirmDeleteNote = (noteId) => {
      Modal.confirm({
        title: 'Delete Note',
        icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
        content: 'Are you sure you want to delete this note? This action cannot be undone.',
        okText: 'Delete',
        okType: 'danger',
        cancelText: 'Cancel',
        onOk() {
          return handleDeleteNote(noteId);
        },
        okButtonProps: {
          loading: isSubmitting
        }
      });
    };

    const startAddNote = (e) => {
      if (e) e.stopPropagation();
      setIsAddingNote(true);
      setEditingNoteId(null);

      form.resetFields();
      form.setFieldsValue({
        type: 'notes',
        notes: ''
      });
    }

    const startEditNote = (e, note) => {
      if (e) e.stopPropagation();

      form.resetFields();
      setIsAddingNote(true);
      setEditingNoteId(note._id);

      form.setFieldsValue({
        type: note.type || 'notes',
        notes: note.notes
      });
    }

    const cancelNote = (e) => {
      if (e) e.stopPropagation();
      setIsAddingNote(false);
      setEditingNoteId(null);
      form.resetFields();
    }

    const submitNote = (e) => {
      if (e) e.stopPropagation();

      form.validateFields().then(values => {
        const noteData = {
          notes: values.notes,
          type: values.type || 'notes'
        };

        if (editingNoteId) {
          handleUpdateNote(editingNoteId, noteData);
        } else {
          handleAddNote(noteData);
        }
      }).catch(error => {
        console.error("Validation failed:", error);
      });
    }

    useEffect(() => {
      if (candidateId) {
        const contextNotes = getCandidateNotes(candidateId);


        if (contextNotes?.length >= 0 && contextNotes.length !== notesData.length) {
          setNotesData(contextNotes);
          setHasNotes(contextNotes.length > 0);

          if (candidate.notesInfo) {
            candidate.notesInfo.notes = contextNotes;
            candidate.notesInfo.notesCount = contextNotes.length;
            candidate.notesInfo.hasNotes = contextNotes.length > 0;
          }
        }
      }
    }, [candidateId, getCandidateNotes]);

    if (!candidate || !candidate.id) {
      console.warn("CandidateCard received invalid candidate data")
      return null
    }

    const isCompared = Array.isArray(compareList) && compareList.includes(candidate.id)

    const handleCardClick = () => {
      setIsLocallyViewed(true)
      onCardClick?.(candidate)
    }

    const handleCompareClick = (e) => {
      e.preventDefault()
      e.stopPropagation()
      onCompare?.(e, candidate.id)
    }

    const renderLocation = () => {
      const locationDetails = candidate.locationDetails || {}
      const parts = [
        locationDetails.city,
        locationDetails.stateName,
        locationDetails.countryName,
        locationDetails.postalCode,
      ].filter(Boolean)

      return parts.length > 0
        ? parts.join(", ")
        : locationDetails.fullName || candidate.location || "Location not specified"
    }

    const formatDate = (dateObj) => {
      if (!dateObj) return ""
      const year = dateObj.Year || ""
      const month = dateObj.Month || ""
      return year && month ? `${month}/${year}` : ""
    }

    const formatSalary = (salary) => {
      if (!salary || (!salary.min && !salary.max)) return ""

      const currency = salary.currency === "0" ? "USD" : salary.currency || "USD"
      const type =
        {
          1: "/yr",
          2: "/hr",
          3: "/wk",
          4: "/mo",
        }[salary.type || "1"] || "/yr"

      if (salary.min === "0" && salary.max === "0") return ""
      if (salary.min === "0") return `Up to ${currency}${salary.max || ""}${type}`
      if (salary.max === "0") return `From ${currency}${salary.min || ""}${type}`
      return `${currency}${salary.min || ""} - ${salary.max || ""}${type}`
    }

    const renderExperience = (position, type = "Current") => {
      if (!position || (!position.title && !position.company)) return null

      return (
        <div style={{ marginBottom: "8px" }}>
          <Space direction="vertical" size={1} style={{ width: "100%" }}>
            <div>
              <Text strong>{type}: </Text>
              {position.title && (
                <>
                  <Text style={useStyles.positionTitle}>{position.title}</Text>
                  {position.company && <Text style={useStyles.companyName}> at {position.company}</Text>}
                </>
              )}
            </div>
            {position.dateFrom && (
              <Text type="secondary" style={{ fontSize: "12px" }}>
                <ClockCircleOutlined style={{ marginRight: "4px" }} />
                {formatDate(position.dateFrom)} - {position.dateTo ? formatDate(position.dateTo) : "Present"}
              </Text>
            )}
            {position.description && (
              <Text type="secondary" style={{ fontSize: "13px" }}>
                {String(position.description).length > 150
                  ? `${String(position.description).substring(0, 150)}...`
                  : position.description}
              </Text>
            )}
          </Space>
        </div>
      )
    }

    const renderTargetInfoSection = () => {
      const targetInfo = candidate.targetInfo || {}

      const hasJobTitle = Boolean(targetInfo.jobTitle)
      const hasSalary = targetInfo.salary && formatSalary(targetInfo.salary)
      const hasRelocation = Boolean(targetInfo.relocation?.status)
      const hasJobTypes = Array.isArray(targetInfo.jobTypes) && targetInfo.jobTypes.length > 0

      if (!hasJobTitle && !hasSalary && !hasRelocation && !hasJobTypes) return null

      return (
        <div style={{ marginBottom: "16px", background: "#f5f5f5", padding: "12px", borderRadius: "6px" }}>
          <Text strong style={{ display: "block", marginBottom: "8px" }}>
            <AimOutlined style={{ marginRight: "8px", color: "#52c41a" }} />
            Target Preferences
          </Text>
          <Space direction="vertical" size={8} style={{ width: "100%" }}>
            {hasJobTitle && (
              <div>
                <Text type="secondary">Desired Role:</Text> <Text strong>{targetInfo.jobTitle}</Text>
              </div>
            )}
            {hasSalary && (
              <div>
                <Text type="secondary">Expected Salary:</Text> <Text strong>{formatSalary(targetInfo.salary)}</Text>
              </div>
            )}
            {hasRelocation && (
              <div>
                <Text type="secondary">Relocation:</Text>{" "}
                <Tag icon={<GlobalOutlined />}>{targetInfo.relocation.status}</Tag>
              </div>
            )}
            {hasJobTypes && (
              <div>
                <Text type="secondary">Preferred Job Types:</Text>
                <div style={{ marginTop: "4px" }}>
                  {targetInfo.jobTypes.map((type, index) => {
                    if (type && typeof type === 'object') {
                      if (type.JobType && typeof type.JobType === 'string') {
                        return (
                          <Tag key={index} style={{ margin: "2px" }}>
                            {type.JobType}
                          </Tag>
                        );
                      }
                      if (type._ && typeof type._ === 'string') {
                        return (
                          <Tag key={index} style={{ margin: "2px" }}>
                            {type._}
                          </Tag>
                        );
                      }
                      return (
                        <Tag key={index} style={{ margin: "2px" }}>
                          Job Type {index + 1}
                        </Tag>
                      );
                    }
                    if (type && typeof type === 'string') {
                      return (
                        <Tag key={index} style={{ margin: "2px" }}>
                          {type}
                        </Tag>
                      );
                    }
                    return null;
                  })}
                </div>
              </div>
            )}
          </Space>
        </div>
      )
    }

    const renderActionBar = () => {
      return (
        <div
          style={{
            ...actionBarStyles,
            opacity: showActions ? 1 : 0,
            transform: `translateY(${showActions ? "0" : "20px"})`,
            pointerEvents: showActions ? "auto" : "none",
          }}
          onClick={(e) => e.stopPropagation()}
          className={cssStyles.actionBar}
        >
          <Tooltip title={isCompared ? "Remove from Compare" : "Add to Compare"}>
            <Button
              icon={<SwapOutlined />}
              size="middle"
              type={isCompared ? "primary" : "default"}
              onClick={handleCompareClick}
            />
          </Tooltip>
          <Tooltip title="Open in New Tab">
            <Button
              icon={<LinkOutlined />}
              size="middle"
              onClick={(e) => {
                e.stopPropagation()
                onExternalView?.(candidate)
              }}
            />
          </Tooltip>
          <Tooltip title={notesExpanded ? "Hide Notes" : "View Notes"}>
            <Button
              icon={<FileTextOutlined />}
              size="middle"
              type={hasNotes || notesExpanded ? "primary" : "default"}
              onClick={toggleNotes}
              badge={{ count: notesData.length, showZero: false }}
            />
          </Tooltip>
        </div>
      );
    }

    const renderMatchPercentage = (matchValue) => {
      if (matchValue === undefined && candidate.relevance === undefined) return null;

      const match = matchValue !== undefined ? matchValue :
        (candidate.relevance !== undefined ? Math.round(candidate.relevance * 100) : null);

      if (match === null) return null;

      let bgColor = "#52c41a";
      if (match < 70) bgColor = "#faad14";
      if (match < 50) bgColor = "#ff4d4f";

      return (
        <div style={{ textAlign: 'right' }}>
          <div
            className={cssStyles.matchPercentageText}
            style={{ backgroundColor: bgColor }}
          >
            {match}% Match
          </div>
          <div className={cssStyles.matchPercentageContainer}>
            <div
              className={cssStyles.matchPercentageFill}
              style={{
                width: `${match}%`,
                backgroundColor: bgColor
              }}
            />
          </div>
        </div>
      );
    };

    const renderNotes = () => {
      return (
        <div
          className={`${cssStyles.notesSection}`}
          onClick={(e) => e.stopPropagation()}
          style={{
            padding: '16px',
            background: themeColors.background,
            borderTop: `1px solid ${themeColors.border}`,
            borderRadius: '0 0 6px 6px'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <Text strong><CommentOutlined /> Notes ({notesData.length})</Text>
            <Button
              type="primary"
              size="small"
              icon={<EditOutlined />}
              onClick={startAddNote}
              disabled={isAddingNote || editingNoteId}
            >
              Add Note
            </Button>
          </div>

          {isAddingNote ? (
            <div style={{ marginBottom: '16px', padding: '12px', border: `1px solid ${themeColors.border}`, borderRadius: '4px', background: '#fff' }}>
              <Form form={form} layout="vertical">
                <Form.Item
                  label="Type"
                  name="type"
                  initialValue="notes"
                  rules={[{ required: true, message: 'Please select a note type' }]}
                >
                  <Radio.Group buttonStyle="solid" defaultValue="notes" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1px', width: '100%' }}>
                    <Radio.Button value="call" style={{ textAlign: 'center', margin: 0 }}>Call</Radio.Button>
                    <Radio.Button value="meeting" style={{ textAlign: 'center', margin: 0 }}>Meeting</Radio.Button>
                    <Radio.Button value="notes" style={{ textAlign: 'center', margin: 0 }}>Note</Radio.Button>
                    <Radio.Button value="others" style={{ textAlign: 'center', margin: 0 }}>Other</Radio.Button>
                  </Radio.Group>
                </Form.Item>

                <Form.Item
                  name="notes"
                  label="Note"
                  rules={[{ required: true, message: 'Please enter note content' }]}
                >
                  <TextArea rows={4} />
                </Form.Item>

                <Row justify="end" gutter={8}>
                  <Col>
                    <Button
                      onClick={cancelNote}
                      icon={<CloseOutlined />}
                    >
                      Cancel
                    </Button>
                  </Col>
                  <Col>
                    <Button
                      type="primary"
                      onClick={submitNote}
                      loading={isSubmitting}
                      icon={<SaveOutlined />}
                    >
                      {editingNoteId ? 'Update' : 'Save'}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </div>
          ) : notesData.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '10px' }}>
              <Text type="secondary">No notes yet. Click "Add Note" to create one.</Text>
            </div>
          ) : (
            <div>
              {notesData.map((note, index) => (
                <div
                  key={note._id || index}
                  style={{
                    marginBottom: '10px',
                    padding: '10px',
                    border: `1px solid ${themeColors.border}`,
                    borderRadius: '4px',
                    background: '#fff'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <Text strong style={{ textTransform: 'capitalize' }}>{note.type || 'notes'}</Text>
                    <Space>
                      <Button
                        type="text"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={(e) => startEditNote(e, note)}
                        disabled={isAddingNote || (editingNoteId && editingNoteId !== note._id)}
                      />
                      <Button
                        type="text"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          confirmDeleteNote(note._id);
                        }}
                        disabled={isSubmitting}
                      />
                    </Space>
                  </div>

                  <div style={{ margin: '10px 0' }}>
                    <Paragraph style={{ whiteSpace: 'pre-wrap' }}>{note.notes}</Paragraph>
                  </div>

                  <div>
                    <Space direction="vertical" size={0}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {moment(note.created_at).format('MMM DD, YYYY [at] hh:mm A')}
                        {note.updated_at && note.created_at !== note.updated_at &&
                          ` • Updated ${moment(note.updated_at).format('MMM DD, YYYY [at] hh:mm A')}`
                        }
                      </Text>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        By: {note.created_by?.display_name || note.created_by?.name || note.created_by_name || 'Unknown'}
                      </Text>
                    </Space>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    };

    return (
      <div
        style={cardWrapperStyles}
        onMouseEnter={() => setShowActions(true)}
        onMouseLeave={() => setShowActions(false)}
        className={`${cssStyles.candidateCardWrapper} ${isSelected ? "selected" : ""}`}
      >
        {renderActionBar()}
        <div
          onClick={handleCardClick}
          className={`${cssStyles.candidateCardHover} ${isLocallyViewed ? cssStyles.candidateCardViewed : ""
            }`}
          style={{
            ...useStyles.candidateCard,
            ...(isSelected ? useStyles.selectedCard : {}),
            ...(candidate.isViewed || isLocallyViewed ? useStyles.viewedCard : {}),
            cursor: "pointer",
            position: "relative",
            borderBottomLeftRadius: notesExpanded ? 0 : '6px',
            borderBottomRightRadius: notesExpanded ? 0 : '6px',
          }}
        >
          {hasNotes && (
            <div
              className={cssStyles.notesIndicator}
              title="Click to view notes"
              onClick={(e) => {
                e.stopPropagation();
                toggleNotes(e);
              }}
              style={{
                top: '8px',
                right: '8px'
              }}
            >
              <FileTextOutlined style={{ fontSize: '10px' }} />
              <span>{notesData.length}</span>
            </div>
          )}

          <div style={useStyles.cardContent}>
            <div style={{ marginBottom: "16px" }}>
              <Row justify="space-between" align="top">
                <Col flex="1">
                  <Space direction="vertical" size={1} style={{ width: "100%" }}>
                    <div style={{
                      ...useStyles.nameContainer,
                      position: 'relative',
                      paddingRight: (candidate.isViewed || isLocallyViewed) ? '80px' : '0'
                    }}>
                      <Text style={{ ...useStyles.positionTitle, fontSize: "16px" }}>
                        {candidate.name || "Unnamed Candidate"}
                      </Text>
                      {(candidate.isViewed || isLocallyViewed) && (
                        <Tag icon={<EyeOutlined />} className={cssStyles.viewedTag}>
                          Viewed
                        </Tag>
                      )}
                    </div>
                    {renderLocation() && (
                      <div style={{ marginTop: '4px' }}>
                        <Text type="secondary">
                          <EnvironmentOutlined style={{ marginRight: "4px" }} />
                          {renderLocation()}
                        </Text>
                      </div>
                    )}
                  </Space>
                </Col>
                <Col>
                  {renderMatchPercentage(candidate.match)}
                </Col>
              </Row>
            </div>

            <Row justify="space-between" align="middle" style={{ marginBottom: "12px" }}>
              <Col>
                <Space>
                  <div>
                    <Checkbox checked={isCompared} onChange={(e) => {
                      e.stopPropagation();
                      handleCompareClick(e);
                    }} />
                  </div>
                  {candidate.resumeTitle && (
                    <div>
                      <Text type="secondary" style={{ fontSize: "12px" }}>
                        ID: {candidate.resumeTitle}
                      </Text>
                    </div>
                  )}
                </Space>
              </Col>
              <Col>
                <Space>
                  {candidate.isInDb && (
                    <div>
                      <Tag icon={<DatabaseOutlined />}>In DB</Tag>
                    </div>
                  )}
                  {candidate.isConfidential && (
                    <div>
                      <Tag icon={<LockOutlined />}>Confidential</Tag>
                    </div>
                  )}
                  {candidate.metadata?.smsOptIn && (
                    <div>
                      <Tag icon={<MessageOutlined />}>SMS Opt-in</Tag>
                    </div>
                  )}
                </Space>
              </Col>
            </Row>

            <Row gutter={[8, 8]} style={{ marginBottom: "16px" }}>
              <Col>
                {candidate.metadata?.isVeteran && (
                  <Tooltip title="Military Status">
                    <Tag icon={<SafetyCertificateOutlined />} style={useStyles.tag}>
                      Veteran
                    </Tag>
                  </Tooltip>
                )}
              </Col>
              <Col>
                {candidate.isConfidential && (
                  <Tooltip title="Confidential Resume">
                    <Tag icon={<LockOutlined />} style={useStyles.tag}>
                      Confidential
                    </Tag>
                  </Tooltip>
                )}
              </Col>
            </Row>

            {renderTargetInfoSection()}

            <div style={{ marginBottom: "16px" }}>
              <Text strong style={{ display: "block", marginBottom: "8px" }}>
                <ClockCircleOutlined style={{ marginRight: "8px", color: "#1890ff" }} />
                Experience
              </Text>
              {candidate.currentPosition && (
                <div
                  style={{
                    marginBottom: "12px",
                    background: "#fff",
                    padding: "12px",
                    border: "1px solid #f0f0f0",
                    borderRadius: "4px",
                  }}
                >
                  <Space direction="vertical" size={2} style={{ width: "100%" }}>
                    {candidate.currentPosition.title && (
                      <div>
                        <Text strong>{candidate.currentPosition.title}</Text>
                      </div>
                    )}

                    <div>
                      <Text type="secondary">
                        {candidate.currentPosition.company && <span>at {candidate.currentPosition.company}</span>}
                        {candidate.currentPosition.dateFrom && candidate.currentPosition.company && <span> • </span>}
                        {candidate.currentPosition.dateFrom && (
                          <span>{formatDate(candidate.currentPosition.dateFrom)} - Present</span>
                        )}
                      </Text>
                    </div>

                    {candidate.currentPosition.description && (
                      <div>
                        <Paragraph
                          type="secondary"
                          ellipsis={{ rows: 2 }}
                          style={{ marginBottom: 0, fontSize: "13px" }}
                        >
                          {candidate.currentPosition.description}
                        </Paragraph>
                      </div>
                    )}
                  </Space>
                </div>
              )}

              {/* Previous Position */}
              {candidate.previousPosition && (
                <div style={{ background: "#fff", padding: "12px", border: "1px solid #f0f0f0", borderRadius: "4px" }}>
                  <Space direction="vertical" size={2} style={{ width: "100%" }}>
                    {candidate.previousPosition.title && (
                      <div>
                        <Text strong>{candidate.previousPosition.title}</Text>
                      </div>
                    )}

                    <div>
                      <Text type="secondary">
                        {candidate.previousPosition.company && <span>at {candidate.previousPosition.company}</span>}
                        {candidate.previousPosition.dateFrom && candidate.previousPosition.company && <span> • </span>}
                        {candidate.previousPosition.dateFrom && (
                          <span>
                            {formatDate(candidate.previousPosition.dateFrom)} -
                            {candidate.previousPosition.dateTo
                              ? formatDate(candidate.previousPosition.dateTo)
                              : "Present"}
                          </span>
                        )}
                      </Text>
                    </div>

                    {candidate.previousPosition.description && (
                      <div>
                        <Paragraph
                          type="secondary"
                          ellipsis={{ rows: 2 }}
                          style={{ marginBottom: 0, fontSize: "13px" }}
                        >
                          {candidate.previousPosition.description}
                        </Paragraph>
                      </div>
                    )}
                  </Space>
                </div>
              )}
            </div>

            <div style={{ marginBottom: "16px" }}>
              <Text strong style={{ display: "block", marginBottom: "8px" }}>
                <SafetyCertificateOutlined style={{ marginRight: "8px", color: "#722ed1" }} />
                Qualifications & Details
              </Text>
              {(candidate.education ||
                candidate.workAuth?.type ||
                candidate.metadata?.isVeteran ||
                candidate.metadata?.isDiversity ||
                candidate.metadata?.smsOptIn) && (
                  <Space wrap style={{ width: "100%" }}>
                    {candidate.education && (
                      <div>
                        <Tag icon={<BookOutlined />}>{candidate.education}</Tag>
                      </div>
                    )}
                    {candidate.workAuth?.type && (
                      <div>
                        <Tag icon={<SafetyCertificateOutlined />}>
                          {candidate.workAuth.type}
                          {candidate.workAuth.country ? ` (${candidate.workAuth.country})` : ""}
                        </Tag>
                      </div>
                    )}
                    {candidate.metadata?.isVeteran && (
                      <div>
                        <Tag icon={<SafetyCertificateOutlined />}>Veteran</Tag>
                      </div>
                    )}
                    {candidate.metadata?.isDiversity && (
                      <div>
                        <Tag icon={<TeamOutlined />}>Diversity</Tag>
                      </div>
                    )}
                    {candidate.metadata?.smsOptIn && (
                      <div>
                        <Tag icon={<MessageOutlined />}>SMS Opt-in</Tag>
                      </div>
                    )}
                  </Space>
                )}
            </div>

            <Divider style={{ margin: "12px 0" }} />
            <Row justify="space-between" align="middle">
              <Col>
                {(candidate.dateModified ||
                  candidate.dateCreated ||
                  candidate.candidateId ||
                  candidate.resumeTitle) && (
                    <Space split={<Divider type="vertical" />} style={{ width: "100%" }}>
                      {candidate.dateModified && (
                        <div>
                          <Text type="secondary" style={{ fontSize: "12px" }}>
                            <CalendarOutlined style={{ marginRight: "4px" }} />
                            Updated {moment(candidate.dateModified).fromNow()}
                          </Text>
                        </div>
                      )}
                      {candidate.dateCreated && (
                        <div>
                          <Text type="secondary" style={{ fontSize: "12px" }}>
                            Created {moment(candidate.dateCreated).format("MM/DD/YYYY")}
                          </Text>
                        </div>
                      )}
                      {candidate.candidateId && (
                        <div>
                          <Text type="secondary" style={{ fontSize: "12px" }}>
                            ID: {candidate.candidateId}
                          </Text>
                        </div>
                      )}
                      {candidate.resumeTitle && (
                        <div>
                          <Text type="secondary" style={{ fontSize: "12px" }}>
                            Resume ID: {candidate.resumeTitle}
                          </Text>
                        </div>
                      )}
                    </Space>
                  )}
              </Col>
              <Col>
                {(candidate.board ||
                  (Array.isArray(candidate.viewHistory) && candidate.viewHistory.length > 0)) && (
                    <Space style={{ width: "100%" }}>
                      {candidate.board && (
                        <div>
                          <Tag style={{ margin: 0 }}>{candidate.board}</Tag>
                        </div>
                      )}
                      {Array.isArray(candidate.viewHistory) && candidate.viewHistory.length > 0 && (
                        <div>
                          <Tooltip title={`Viewed ${candidate.viewHistory.length} times`}>
                            <Tag icon={<HistoryOutlined />}>{candidate.viewHistory.length} Views</Tag>
                          </Tooltip>
                        </div>
                      )}
                    </Space>
                  )}
              </Col>
            </Row>
          </div>
        </div>

        {notesExpanded && renderNotes()}
      </div>
    )
  },
)

CandidateCard.propTypes = {
  candidate: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    location: PropTypes.string,
    locationDetails: PropTypes.object,
    currentPosition: PropTypes.object,
    previousPosition: PropTypes.object,
    metadata: PropTypes.object,
    targetInfo: PropTypes.object,
    education: PropTypes.string,
    workAuth: PropTypes.object,
    dateModified: PropTypes.string,
    dateCreated: PropTypes.string,
    isConfidential: PropTypes.bool,
    isViewed: PropTypes.bool,
    board: PropTypes.string,
    viewHistory: PropTypes.array,
    resumeTitle: PropTypes.string,
    candidateId: PropTypes.string,
    relevance: PropTypes.number,
    isInDb: PropTypes.bool,
    notes: PropTypes.array,
    notesLocation: PropTypes.string,
    candidateDbId: PropTypes.string,
  }).isRequired,
  isSelected: PropTypes.bool,
  onCardClick: PropTypes.func,
  onCompare: PropTypes.func,
  compareList: PropTypes.arrayOf(PropTypes.string),
  onAddToDb: PropTypes.func,
  onExternalView: PropTypes.func,
  useStyles: PropTypes.object,
}

CandidateCard.displayName = "CandidateCard"

export default CandidateCard

